import React, {useState} from "react"
import Layout from "../components/layout"
import HeroImageContainer from "../components/heroImage"

import prostate101Banner from "../images/prostate-101-banner.png"
import normalProstate from "../images/normal-prostate.svg"
import enlargedProstate from "../images/enlarged-prostate.svg"

import prostateRisks from "../images/prostate-risks.png"
import prostateSigns from "../images/prostate-signs.png"

import prostateAge from "../images/prostate-age.svg"
import prostateRaceEth from "../images/prostate-race-eth.svg"
import prostateGeo from "../images/prostate-geo.svg"
import prostateFamHistory from "../images/prostate-fam-history.svg"
import circleOfCare from "../images/Circle-of-Care.png"

let title = `What is Prostate Cancer?`
let desc = `Prostate cancer is the second most common cancer in men. Talk to a board-certified radiologist and learn early signs & symptoms, prostate MRI, PET/CT scans and more.`

const Prostate101 = () => {
    let stats = [
        {
            headline: `1 in 8`,
            body: `men will be diagnosed with prostate cancer during his lifetime`
        },
        {
            headline: `70%`,
            body: `the number of new cases diagnosed in Black men is 70% higher than the number of new cases diagnosed in White men. Black men are more than twice as likely to die from prostate cancer than other men`
        },
        {
            headline: `6 in 10`,
            body: `prostate cancers diagnosed in men who are 65 or older. Prostate cancer risk is also higher in African American men and in Caribbean men of African ancestry than in men of other races`
        },
        {
            headline: `333,830`,
            body: `the estimated number of new prostate cancer cases amongst American men in 2025`
        },
        {
            headline: `36,320`,
            body: `an estimate on the number of American men who will die of prostate cancer in 2025`
        },
        {
            headline: `67`,
            body: `average age of a prostate cancer diagnosis in America`
        },
        {
            headline: `3.3`,
            body: `million men in the United States who have been diagnosed with prostate cancer are still alive today`
        },
        {
            headline: `#2`,
            body: `after skin cancer more American men are diagnosed with prostate cancer than any other type of cancer.`
        }
    ]

    let statsOut = stats.map((item, key)=>{
        let {headline, body} = item
        return (
            <div key={key} className="w-100 tc tl-l w-third-l pa2">
                <h4 className="mb0 tc tl-l" style={{color: `#66CCFF`, fontSize: `3rem`}}>{headline}</h4>
                <p className="mt2 mt0-l tc tl-l" style={{color: `#616871`, fontWeight: `300`}}>{body}</p>
            </div>
        )
    })


    let risks = [
        {
            icon: prostateAge,
            headline: `Age`,
            body: `The chance of having prostate cancer rises rapidly after the age of 50. About 6 out of 10 cases of prostate cancer are found in men older than 65.`
        },
        {
            icon: prostateRaceEth,
            headline: `Race/Ethnicity`,
            body: ` While the reasons are unclear, prostate cancer seems to develop more often in African-American men and in Caribbean men of African ancestry than in men of other races. And when it does develop in these men, they tend to be younger. Prostate cancer also occurs less often in Asian-American and Hispanic/Latino men than in Non- Hispanic whites.`
        },
        {
            icon: prostateGeo,
            headline: `Geography`,
            body: `Again it is unclear as to why, but prostate cancer is most common in North America, northwestern Europe, Australia, and on Caribbean Islands. Reasons for this may be due to lifestyle and diet, or because more intensive screening methods exist in these areas.`
        },
        {
            icon: prostateFamHistory,
            headline: `Family History`,
            body: `Prostate cancer seems to run in some families, although many people do get it without any family history. Nonetheless, having a father or brother with prostate cancer more than doubles a man’s risk of developing the disease.`
        }

    ]

    let risksOut = risks.map((item, key)=>{
        let {icon, headline, body} = item
        return (
            <div key={key} className="flex flex-wrap">
                <div className={`w-100 tc tr-l w-25-l pr4-l`}>
                    <p><img src={icon} alt={headline} style={{height:`58px`,width:`auto`}} /></p>
                </div>
                <div className={`w-100 tj tl-l w-75-l`}>
                    <h3 className="tc tl-l">{headline}</h3>
                    <p>{body}</p>
                </div>
            </div>
        )
    })

    let [normalActive, setNormalActive] = useState(true)
    let [enlargedActive, setEnlargedActive] = useState(false)


    return (
        <Layout page={title} desc={desc}>
            <div className="">
                <HeroImageContainer imageUrl={prostate101Banner} />
                <div className="mt3 pb6 container-inner-xs container-inner-m container-inner">
                    <div className="tc center-block" style={{maxWidth: `640px`}}>
                        <h1>What is Prostate Cancer?</h1>
                        <p>Prostate cancer begins when cells in the prostate gland start to grow out of control.  Some prostate cancers grow and spread quickly, but most grow slowly.</p>

                        <div className="button-set">
                            <button className={`${normalActive ? 'cta':''}`} onClick={()=>{
                                setNormalActive(true)
                                setEnlargedActive(false)
                            }}>Normal Prostate</button><button className={`${enlargedActive ? 'cta': ''}`} onClick={()=>{
                                setEnlargedActive(true)
                                setNormalActive(false)
                            }}>Enlarged Prostate</button>
                        </div>
                        <p className={`${normalActive ? 'show': 'hide'} mt4`}><img className='prostate-diagram' src={normalProstate} alt="Diagram" /></p>
                        <p className={`${enlargedActive ? 'show' : 'hide'} mt4`}><img className='prostate-diagram' src={enlargedProstate} alt="Diagram" /></p>
                    </div>

                    <h2>By the Numbers</h2>
                    <p>Prostate cancer is the second most common cancer in men, affecting 1 in 8 men. Despite the prevalence of prostate cancer, it is highly treatable if detected early. Today, 3.3 million men living in the United States have been diagnosed with prostate cancer at some point.</p>
                    <div className="flex flex-wrap">
                        {statsOut}
                    </div>
                    <p className="tr pt4 pb4" style={{color:`#2A5BAA`, fontSize:`13px`}}>(sources: American Cancer Society, updated 2024; Cancer.net, 2023; CDC, July, 2023)</p>

                    <p><img src={prostateRisks} alt="Risks" style={{width: `100%`}} /></p>
                    <h2>What are the Causes/Risks of Prostate Cancer?</h2>
                    <p>While there is no known cause for Prostate Cancer, there are several risk factors that researchers have identified. Having a risk factor, or even several risk factors, does not mean that you will get the disease. Many people with one or several risk factors never get cancer, while others who are diagnosed with prostate cancer may have had few or no known risk factors. </p>
                    <p>These risk factors include:</p>
                    <div>
                        {risksOut}
                    </div>


                    <p className="tr pt4 pb4" style={{color:`#2A5BAA`, fontSize:`13px`}}>According to the American Cancer Society</p>
                    <p>There are other factors with a less clear effect on the risk for prostate cancer, but should be considered regardless. These include smoking, diet and obesity, all of which have been shown to contribute to the risk for various cancers.</p>

                    <p><img src={prostateSigns} alt="Symptoms" style={{width:`100%`}} /></p>
                    <h2>What are the Signs/Symptoms of Prostate Cancer?</h2>
                    <p>In its early stages, there are generally no signs of prostate cancer. The most effective way to find it early is through regular screening with your physician who will test your prostate specific antigen (PSA) levels. If these levels are high, it often indicates a potential for prostate cancer.</p>
                    <h2>What are Treatment Options for Prostate Cancer?</h2>
                    <p>Treatment of prostate cancer varies depending on the type of cancer with which a patient has been diagnosed.
                    Some prostate cancers are small in size and slow-growing. These only need to be carefully monitored - they are under “active surveillance.”</p>

                    <p>If a prostate cancer requires additional treatment, there are several traditional options available including the following:</p>
                    <ul>
                        <li>Gland Ablation/TULSA</li>
                        <li>Radiation</li>
                        <li>Robotic Prostatectomy</li>
                        <li>Complete gland removal</li>
                        <li>Chemotherapy/hormone therapy</li>
                    </ul>

                    <p>Most of these therapies carry potential negative side effects, primarily urinary incontinence and erectile dysfunction. However, THE TULSA PROCEDURE offers an incision-free alternative to treating prostate cancer, without the risk for adverse side effects.
                    </p>
                    <a href="/tulsa" target="_self">LEARN MORE ABOUT TULSA PRO</a>
                    <h2>Early Detection & Survival Rates</h2>
                    <p>While prostate cancer is relatively common, the good news is that treatment success rates are high compared with many other types of cancer.</p>
                    <h4>The 5-year survival rate in the United States for men diagnosed with early-stage prostate cancer is greater than 99%.</h4>
                    <p>In other words, the chance of a man dying from his prostate cancer is generally low. However, prostate cancer comes in many forms, and some men can have aggressive prostate cancer even when it appears to be confined to the prostate.</p>
                    <p>Overall, the earlier prostate cancer is caught and treated, the better the chances are for survival and better outcomes.</p>
                    <a href="/enhanced-prostate-screening" target="_self">LEARN MORE ABOUT ENHANCED PROSTATE SCREENING</a>
                </div>
            </div>
            <div className="container-inner mt5 mb6">
                <div className="tc">
                    <h2>Circle of Care</h2>
                    <img src={circleOfCare} alt="Circle of Care" className="circle-of-care-img" />
                </div>
                <div className="mt4">
                    <div className="flex flex-wrap">
                        <div className="w-100 w-50-l pa3">
                            <h3>1. Prostate MRI</h3>
                            <p>bpMRI (Screening) or mpMRI (Diagnostic)</p>
                            <p>Prostate MRI plays key roles in many steps of prostate cancer management including detection and diagnosis of prostate cancer. The detailed images show the prostate gland and the surrounding tissues.</p>
                            <p><small>*NIH 2022 and 2023</small></p>
                        </div>
                        <div className="w-100 w-50-l pa3">
                            <h3>2. MRI Fusion Biopsy</h3>
                            <p>MRI Fusion Biopsy helps diagnose, stage, and treat prostate cancer. The fused MRI and 3D ultrasound images produced at AZDRG are used by Urologists to take targeted tissue samples (biopsy) from the prostate gland.</p>
                            <p>We target any Pi-Rads 3, 4, or 5 lesions identified and integrate  with many Vendors and software’s. (see list on back).</p>
                            <p><small>*Cleveland Clinic 2024</small></p>
                        </div>
                        <div className="w-100 w-50-l pa3">
                            <h3>3. PET/CT PSMA</h3>
                            <p>F-18 PSMA/PYL: Prostate-specific membrane antigen is a protein expressed in all prostate tissue. 95% of prostate cancer cells overexpress PSMA, making it an excellent imaging marker for prostate cancer and metastases.</p>
                        </div>
                        <div className="w-100 w-50-l pa3">
                            <h3>4. TULSA-PRO</h3>
                            <p>The Transurethral Ultrasound Ablation (TULSA) Procedure is a minimally invasive procedure that uses directional ultrasound to produce very high temperatures to ablate (destroy) targeted prostate tissue.</p>
                            <p>The procedure combines real-time MRI with robotically-driven, directional thermal ultrasound to deliver predictable, physician-prescribed ablation of the whole gland or partial prostate tissue.</p>
                        </div>
                        <div className="w-100 w-50-l center pa3">
                            <h3>5. Post TULSA & Procedure Monitoring</h3>
                            <p>Post procedure monitoring can be achieved with either MRI or PSMA-PET/CT</p>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    )
}

export default Prostate101
